import { Link, useLocation } from "react-router-dom";
import { Check, Minus, ArrowRight, Clock } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { PACKAGES, CORE_SERVICES, COMPARISON, PACKAGE_NOTES, FUTURE_ADDITIONAL_SERVICES } from "@/data/site";
import { trackEvent } from "@/lib/track";

const BTN = "mt-auto inline-flex items-center justify-center gap-3 w-full min-h-[48px] px-6 py-3.5 font-mono text-[13px] uppercase tracking-[0.14em] border";

const PackageCard = ({ pkg, index }) => {
  const { pathname } = useLocation();
  const soon = !pkg.available;
  return (
    <Reveal delay={index * 0.08} className="h-full">
      <article data-testid={`package-${pkg.id}`} data-available={pkg.available ? "true" : "false"}
        className={`h-full flex flex-col border p-8 lg:p-10 transition-[border-color,transform,box-shadow] duration-300 ease-out ${
          soon ? "bg-cream border-dashed border-[#C9BFA8]" : "bg-cream-ivory border-[#D9CFB8] hover:border-[#8a6b2f]/50 motion-safe:hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-28px_rgba(11,31,59,0.4)]"
        }`}>
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 min-w-0">
          <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f] whitespace-nowrap">{pkg.name}</p>
          {soon ? (
            <span data-testid="package-full-badge" className="inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.18em] text-navy bg-gold/25 border border-gold/60 px-2.5 py-1">
              <Clock size={12} aria-hidden="true" /> Coming Soon
            </span>
          ) : (
            <span className="whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.18em] text-[#4f6b3a] border border-[#9cb48a] px-2.5 py-1">Available now</span>
          )}
        </div>
        <p className="mt-5 flex items-baseline gap-2">
          <span data-testid={`package-${pkg.id}-price`} className="font-serif text-5xl lg:text-6xl text-navy tracking-tight">{pkg.price}</span>
          <span className="font-mono text-[12px] uppercase tracking-[0.16em] text-ink-muted">per month</span>
        </p>
        <p data-testid={`package-${pkg.id}-tagline`} className="mt-4 font-serif text-xl text-navy leading-snug">{pkg.tagline}</p>
        {soon && (
          <p data-testid="package-full-notice" className="mt-3 text-[15px] text-ink leading-snug">
            Not currently available. Preview our planned full-service package.
          </p>
        )}
        <span className="block h-px bg-[#C5A059] w-12 mt-6 mb-6" />

        <ul className="space-y-2.5 mb-6">
          {pkg.limits.map((item) => (
            <li key={item} className="font-mono text-[12px] uppercase tracking-[0.1em] text-navy">{item}</li>
          ))}
        </ul>
        <ul className="space-y-3 mb-10">
          {pkg.features.map((item) => {
            const excluded = item.startsWith("Actual bill payment");
            return (
              <li key={item} className={`flex items-start gap-3 text-[15px] leading-snug ${excluded ? "text-ink-muted" : "text-ink"}`}>
                {excluded ? <Minus size={15} className="shrink-0 mt-[3px]" aria-hidden="true" /> : <Check size={15} className="text-[#8a6b2f] shrink-0 mt-[3px]" aria-hidden="true" />}
                {item}
              </li>
            );
          })}
        </ul>

        {soon ? (
          <button type="button" disabled aria-disabled="true" data-testid={`package-${pkg.id}-cta`}
            className={`${BTN} bg-transparent text-ink-muted border-[#C9BFA8] cursor-not-allowed`}>
            Coming Soon
          </button>
        ) : (
          <Link to={`/contact?package=${pkg.id}`} data-testid={`package-${pkg.id}-cta`}
            onClick={() => trackEvent("package", pathname, pkg.id)}
            className={`${BTN} group bg-navy text-parchment border-gold/30 hover:bg-navy-2 hover:-translate-y-0.5 transition-all duration-300`}>
            {pkg.cta} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        )}
      </article>
    </Reveal>
  );
};

const Cell = ({ v }) => {
  if (v === true) return <Check size={16} className="text-[#8a6b2f] mx-auto" aria-label="Included" />;
  if (v === false) return <Minus size={16} className="text-ink-muted/60 mx-auto" aria-label="Not included" />;
  return <span className="text-[14px] text-ink leading-snug">{v}</span>;
};

const Comparison = () => (
  <Reveal>
    <div className="mt-20">
      <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-6">Service-by-service comparison</p>
      <div data-testid="package-comparison" className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-left">
          <thead>
            <tr className="border-b border-[#C9BFA8]">
              <th scope="col" className="py-4 pr-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#8a6b2f] font-normal w-[40%]">Service</th>
              {PACKAGES.map((p) => (
                <th key={p.id} scope="col" className="py-4 px-3 text-center font-normal w-[20%]">
                  <span className="block font-serif text-xl text-navy">{p.name}</span>
                  <span className="block mt-1 font-mono text-[11px] uppercase tracking-[0.16em] text-ink-muted">{p.price}/mo{p.available ? "" : " · coming soon"}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARISON.map(([label, ...vals]) => (
              <tr key={label} className="border-b border-[#E2DACB] hover:bg-cream-ivory transition-colors">
                <th scope="row" className="py-3.5 pr-4 text-[15px] text-ink font-normal leading-snug">{label}</th>
                {vals.map((v, i) => <td key={i} className={`py-3.5 px-3 text-center ${i === 2 ? "text-ink-muted" : ""}`}><Cell v={v} /></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </Reveal>
);

export const Packages = () => (
  <section data-testid="packages-section" className="bg-cream pt-28 pb-24">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-5">Pricing</p>
        <h2 className="font-serif text-4xl lg:text-5xl text-navy tracking-tight leading-tight">Monthly Bookkeeping Packages</h2>
        <p className="mt-5 text-ink-muted text-lg leading-relaxed max-w-2xl">Choose a fixed monthly package based on your transaction volume and accounts.</p>
      </Reveal>

      <Reveal>
        <div data-testid="core-services" className="mt-10 border-l-2 border-[#C5A059] pl-5 lg:pl-6">
          <p className="text-ink text-[17px] leading-relaxed">Every monthly package includes complete monthly bookkeeping:</p>
          <ul className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
            {CORE_SERVICES.map((c) => (
              <li key={c} className="flex items-start gap-2.5 text-[15px] text-ink leading-snug">
                <Check size={15} className="text-[#8a6b2f] shrink-0 mt-[3px]" aria-hidden="true" /> {c}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>

      <div className="mt-14 grid md:grid-cols-3 gap-6 items-stretch">
        {PACKAGES.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} />)}
      </div>

      <Comparison />

      <Reveal>
        <div data-testid="future-additional-services" className="mt-14 border border-dashed border-[#C9BFA8] p-8 lg:p-10 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4">
            <h3 className="font-serif text-2xl text-navy">Future Additional Services</h3>
            <p data-testid="future-additional-badge" className="mt-3 inline-block font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted border border-[#C9BFA8] px-3 py-1.5">Not Currently Available</p>
          </div>
          <div className="lg:col-span-8">
            <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-3">
              {FUTURE_ADDITIONAL_SERVICES.map((o) => (
                <li key={o} className="flex items-start gap-3 text-ink-muted text-[15px]">
                  <Minus size={15} className="shrink-0 mt-[3px]" aria-hidden="true" /> {o}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-[15px] text-ink-muted">These are not part of any package, including the planned Full Service package.</p>
          </div>
        </div>
      </Reveal>

      <Reveal>
        <div data-testid="package-details" className="mt-16 border-t border-[#D9CFB8] pt-10 grid lg:grid-cols-12 gap-8">
          <p className="lg:col-span-3 font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f]">Package details</p>
          <ul className="lg:col-span-9 grid sm:grid-cols-2 gap-x-10 gap-y-4">
            {PACKAGE_NOTES.map((n) => (
              <li key={n} className="flex items-start gap-3 text-ink text-[15px] leading-relaxed">
                <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0" aria-hidden="true" /> {n}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  </section>
);
